import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, Utensils } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const wasSessionExpired = new URLSearchParams(location.search).get('session') === 'expired'
    || (typeof window !== 'undefined' && sessionStorage.getItem('auth_session_expired') === '1');

  useEffect(() => {
    if (wasSessionExpired) {
      try { sessionStorage.removeItem('auth_session_expired'); } catch { /* ignore */ }
      toast({
        title: 'Session expired',
        description: 'Your login expired after 24 hours — please sign in again.',
        variant: 'destructive',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast({ title: 'Welcome back!', description: 'Login successful' });
        // Bounce back to the page the user was on before their session expired
        try {
          const stashed = sessionStorage.getItem('post_login_redirect');
          if (stashed) {
            sessionStorage.removeItem('post_login_redirect');
            navigate(stashed, { replace: true });
          }
        } catch { /* ignore */ }
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast({ title: 'Passwords do not match', variant: 'destructive' });
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast({ title: 'Password must be at least 6 characters', variant: 'destructive' });
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast({ title: 'Account created!', description: 'Welcome to Willow Ridge' });
      }
    } catch (error) {
      toast({ 
        title: isLogin ? 'Login failed' : 'Registration failed', 
        description: error.response?.data?.detail || error.message,
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#2a4a6f] to-[#152a45] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#c9a227] rounded-full mb-4">
            <Utensils size={32} className="text-[#1e3a5f]" />
          </div>
          <h1 className="text-3xl font-bold text-white">Willow Ridge</h1>
          <p className="text-[#c9a227] mt-1">Catering Management System</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-[#1e3a5f]">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? 'Enter your credentials to access your account' 
                : 'Set up your admin account to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    className="mt-1"
                    data-testid="name-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required={!isLogin}
                    className="mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white h-11"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              Willow Ridge Catering Management
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-white/60 text-sm mt-6">
          © {new Date().getFullYear()} Willow Ridge Catering
        </p>
      </div>
    </div>
  );
};

export default Login;
